import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api';
import { t } from '../../i18n';
import { APP_LOGO, APP_LOGO_ALT } from '../../config/brand';

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: 输入邮箱, 2: 输入验证码和新密码
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const resendLabel = countdown > 0
    ? t('reset_password_resend_countdown', { seconds: countdown })
    : t('reset_password_resend');

  // 监控步骤变化
  useEffect(() => {
    // 可以在这里添加步骤变化的逻辑
  }, [step]);

  // 组件挂载和卸载调试
  useEffect(() => {
    // 可以在这里添加组件挂载时的逻辑
    return () => {
      // 可以在这里添加组件卸载时的清理逻辑
    };
  }, []);

  // 倒计时功能
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendCode = async () => {
    if (!email || !email.trim()) {
      setError(t('reset_password_error_enter_email'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('reset_password_error_invalid_email'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('users/send-reset-code', { email });
      const msg = response.data?.message || t('reset_password_code_sent');
      setSuccess(msg);
      setStep(2);
      setCountdown(60);
    } catch (err) {
      // 更详细的错误处理
      let errorMessage = err.response?.data?.error
        || err.response?.data?.message
        || err.message
        || t('reset_password_error_send_failed');

      if (typeof errorMessage === 'string') {
        const lowerMessage = errorMessage.toLowerCase();
        if (errorMessage.includes('未注册') || lowerMessage.includes('not registered')) {
          errorMessage = t('reset_password_error_email_unregistered');
        } else if (errorMessage.includes('频繁') || lowerMessage.includes('frequent')) {
          errorMessage = t('reset_password_error_too_frequent');
        } else if (errorMessage.includes('邮箱不能为空') || lowerMessage.includes('email') && lowerMessage.includes('required')) {
          errorMessage = t('reset_password_error_enter_email');
        }
      }

      setError(errorMessage || t('reset_password_error_send_failed'));
      // 确保在错误时不清除状态
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!code || !newPassword || !confirmPassword) {
      setError(t('reset_password_error_required_fields'));
      return;
    }

    if (newPassword.length < 6) {
      setError(t('reset_password_error_password_length'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('reset_password_error_password_mismatch'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('users/reset-password', {
        email,
        code,
        newPassword
      });
      setSuccess(t('reset_password_success'));
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      const message = err.response?.data?.error || err.message;
      setError(message || t('reset_password_error_reset_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    if (countdown > 0) return;
    handleSendCode();
  };

  return (
    <div className="min-h-screen min-w-[320px] flex items-center justify-center bg-white px-4">
      <div className="w-full max-w-sm mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8 min-w-[280px] min-h-[400px] flex flex-col justify-center relative">
          <div className="text-center mb-8">
            <img
              src={APP_LOGO}
              alt={APP_LOGO_ALT}
              className="h-16 w-auto object-contain mx-auto mb-3"
            />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('systemName')}</h1>
            <h2 className="text-lg font-medium text-gray-600">
              {t(step === 1 ? 'reset_password_step1_title' : 'reset_password_step2_title')}
            </h2>
            <div className="text-xs text-gray-400 mt-2">
              {t('reset_password_step_info', { step, email: email || t('reset_password_email_not_provided') })}
            </div>
          </div>

          {step === 1 ? (
            // 第一步：输入邮箱
            <div className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('reset_password_email_label')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('reset_password_email_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {error && (
                <div className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-md p-3">
                  {error}
                </div>
              )}

              {success && (
                <div className="text-green-500 text-sm bg-green-50 border border-green-200 rounded-md p-3">
                  {success}
                </div>
              )}

              <button
                onClick={handleSendCode}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('reset_password_sending') : t('reset_password_send_button')}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-blue-600 hover:text-blue-800 hover:underline text-sm transition-colors duration-200"
                >
                  {t('reset_password_back_to_login')}
                </Link>
              </div>
            </div>
          ) : (
            // 第二步：输入验证码和新密码
            <div className="space-y-6">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('reset_password_code_label')}
                </label>
                <div className="flex space-x-2">
                  <input
                    id="code"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder={t('reset_password_code_placeholder')}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    maxLength={6}
                    required
                  />
                  <button
                    onClick={handleResendCode}
                    disabled={countdown > 0 || loading}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {resendLabel}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('reset_password_new_password_label')}
                </label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('reset_password_new_password_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('reset_password_confirm_password_label')}
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('reset_password_confirm_password_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {error && (
                <div className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-md p-3">
                  {error}
                </div>
              )}

              {success && (
                <div className="text-green-500 text-sm bg-green-50 border border-green-200 rounded-md p-3">
                  {success}
                </div>
              )}

              <button
                onClick={handleResetPassword}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('reset_password_resetting') : t('reset_password_reset_button')}
              </button>

              <div className="text-center">
                <button
                  onClick={() => setStep(1)}
                  className="text-blue-600 hover:text-blue-800 hover:underline text-sm transition-colors duration-200"
                >
                  {t('reset_password_back_step')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage; 